import { getToken, clearToken } from '../utils/authToken';
/**
 * MyApps - Galería de Apps Publicadas del Usuario
 * Sistema completo de gestión de apps publicadas
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { 
  X, Plus, Globe, Lock, Eye, Heart, Copy, Trash2, Edit3, 
  ExternalLink, Clock, Calendar, Tag, Search, Filter,
  MoreVertical, Share2, Code, Play, Loader2, Check,
  FolderOpen, Rocket, Archive, RefreshCw, Star, MessageSquare,
  Wand2
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import StarRating from './StarRating';
import AppComments from './AppComments';
import { FEATURE_FLAGS } from '../config/featureFlags';

import { API_URL } from '../config';

// ==================== APP CARD ====================
const AppCard = ({ app, onView, onEdit, onClone, onDelete, onTogglePublic, isOwner = true }) => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const [showMenu, setShowMenu] = useState(false);
  
  const statusColors = {
    draft: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    published: 'bg-green-500/20 text-green-400 border-green-500/30',
    archived: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'
  };
  
  const statusLabels = {
    draft: isSpanish ? 'Borrador' : 'Draft',
    published: isSpanish ? 'Publicada' : 'Published',
    archived: isSpanish ? 'Archivada' : 'Archived'
  };

  return (
    <div className="bg-zinc-900/80 border border-zinc-700/50 rounded-xl overflow-hidden hover:border-indigo-500/50 transition-all group">
      {/* Thumbnail / Preview */}
      <div className="relative h-36 bg-zinc-800 overflow-hidden">
        {app.thumbnail ? (
          <img src={app.thumbnail} alt={app.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-900/50 to-purple-900/50">
            <Code className="w-10 h-10 text-indigo-400/50" />
          </div>
        )}
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <button
            onClick={() => onView(app)}
            className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            title={isSpanish ? 'Ver app' : 'View app'}
          >
            <Eye className="w-5 h-5 text-white" />
          </button>
          {isOwner && (
            <button
              onClick={() => onEdit(app)}
              className="p-2 bg-indigo-500/30 hover:bg-indigo-500/50 rounded-lg transition-colors"
              title={isSpanish ? 'Abrir en Builder' : 'Open in Builder'}
              data-testid="myapps-edit-btn"
            >
              <Wand2 className="w-5 h-5 text-white" />
            </button>
          )}
          <button
            onClick={() => onClone(app)}
            className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            title={isSpanish ? 'Clonar' : 'Clone'}
          >
            <Copy className="w-5 h-5 text-white" />
          </button>
        </div>
        
        {/* Status Badge */}
        <div className={`absolute top-2 left-2 px-2 py-0.5 text-[10px] font-medium rounded-full border ${statusColors[app.status]}`}>
          {statusLabels[app.status]}
        </div>
        
        {/* Public/Private Badge */}
        <div className="absolute top-2 right-2">
          {app.is_public ? (
            <Globe className="w-4 h-4 text-green-400" />
          ) : (
            <Lock className="w-4 h-4 text-yellow-400" />
          )}
        </div>
      </div>
      
      {/* Info */}
      <div className="p-3">
        <h3 className="text-sm font-semibold text-white truncate mb-1">{app.name}</h3>
        <p className="text-xs text-zinc-400 line-clamp-2 h-8 mb-2">
          {app.description || (isSpanish ? 'Sin descripción' : 'No description')}
        </p>
        
        {/* Tags */}
        {app.tags && app.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {app.tags.slice(0, 3).map((tag, i) => (
              <span key={i} className="px-1.5 py-0.5 text-[9px] bg-indigo-500/20 text-indigo-300 rounded">
                {tag}
              </span>
            ))}
          </div>
        )}
        
        {/* Stats */}
        <div className="flex items-center justify-between text-[10px] text-zinc-500">
          <div className="flex items-center gap-3">
            {/* Star Rating Display */}
            {(app.average_rating > 0 || app.ratings_count > 0) && (
              <span className="flex items-center gap-1 text-amber-400">
                <Star className="w-3 h-3 fill-amber-400" />
                {app.average_rating?.toFixed(1) || '0.0'}
                <span className="text-zinc-500">({app.ratings_count || 0})</span>
              </span>
            )}
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" /> {app.views_count || 0}
            </span>
            <span className="flex items-center gap-1">
              <Heart className="w-3 h-3" /> {app.likes_count || 0}
            </span>
            <span className="flex items-center gap-1">
              <MessageSquare className="w-3 h-3" /> {app.comments_count || 0}
            </span>
          </div>
          
          {isOwner && (
            <div className="relative">
              <button 
                onClick={() => setShowMenu(!showMenu)}
                className="p-1 hover:bg-zinc-700 rounded transition-colors"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              
              {showMenu && (
                <div className="absolute right-0 bottom-6 w-36 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-10 py-1">
                  <button
                    onClick={() => { onTogglePublic(app); setShowMenu(false); }}
                    className="w-full px-3 py-1.5 text-left text-xs hover:bg-zinc-700 flex items-center gap-2"
                  >
                    {app.is_public ? <Lock className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                    {app.is_public ? (isSpanish ? 'Hacer privada' : 'Make private') : (isSpanish ? 'Hacer pública' : 'Make public')}
                  </button>
                  <button
                    onClick={() => { onDelete(app); setShowMenu(false); }}
                    className="w-full px-3 py-1.5 text-left text-xs hover:bg-red-900/50 text-red-400 flex items-center gap-2"
                  >
                    <Trash2 className="w-3 h-3" />
                    {isSpanish ? 'Eliminar' : 'Delete'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== APP VIEWER MODAL ====================
const AppViewerModal = ({ app, isOpen, onClose, onClone }) => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(app?.likes_count || 0);
  const [userRating, setUserRating] = useState(0);
  const [averageRating, setAverageRating] = useState(app?.average_rating || 0);
  const [ratingsCount, setRatingsCount] = useState(app?.ratings_count || 0);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const isAuthenticated = !!getToken();
  
  // Load user's existing rating when modal opens
  useEffect(() => {
    if (isOpen && app) {
      setAverageRating(app.average_rating || 0);
      setRatingsCount(app.ratings_count || 0);
      setLikesCount(app.likes_count || 0);
      checkUserRating();
      checkUserLike();
    }
  }, [isOpen, app]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const checkUserRating = async () => {
    try {
      const token = getToken();
      if (!token || !app) return;
      
      const response = await axios.get(
        `${API_URL}/api/published-apps/check-rating/${app.id}`,
        {}
      );
      
      if (response.data.has_rated) {
        setUserRating(response.data.user_rating);
      }
    } catch (error) {
      // Silent fail
    }
  };
  
  const checkUserLike = async () => {
    try {
      const token = getToken();
      if (!token || !app) return;
      
      const response = await axios.get(
        `${API_URL}/api/published-apps/check-like/${app.id}`,
        {}
      );
      
      setLiked(response.data.liked);
    } catch (error) {
      // Silent fail
    }
  };
  
  const handleRate = async (rating) => {
    const token = getToken();
    if (!token) {
      toast.error(isSpanish ? 'Inicia sesión para calificar' : 'Login to rate');
      return;
    }
    
    setRatingLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/published-apps/${app.id}/rate`,
        { rating },
        {}
      );
      
      setUserRating(rating);
      setAverageRating(response.data.average_rating);
      setRatingsCount(response.data.ratings_count);
      toast.success(isSpanish ? '¡Gracias por tu calificación!' : 'Thanks for your rating!');
    } catch (error) {
      toast.error(isSpanish ? 'Error al calificar' : 'Error rating app');
    } finally {
      setRatingLoading(false);
    }
  };
  
  const handleLike = async () => {
    const token = getToken();
    if (!token) {
      toast.error(isSpanish ? 'Inicia sesión para dar like' : 'Login to like');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/api/published-apps/${app.id}/like`,
        {},
        {}
      );
      setLiked(response.data.liked);
      setLikesCount(response.data.likes_count);
    } catch (error) {
      toast.error(isSpanish ? 'Error al dar like' : 'Error liking app');
    }
  };
  
  const handleShare = async () => {
    const url = `${window.location.origin}/app/${app.slug}`;
    await navigator.clipboard.writeText(url);
    toast.success(isSpanish ? '¡Enlace copiado!' : 'Link copied!');
  };

  if (!isOpen || !app) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      
      <div 
        className="relative w-full max-w-5xl h-[90vh] bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-zinc-700 bg-zinc-800/50">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center">
              <Rocket className="w-4 h-4 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">{app.name}</h2>
              <p className="text-[10px] text-zinc-400">
                {isSpanish ? 'Por' : 'By'} {app.owner_name}
              </p>
            </div>
            
            {/* Rating Display & Interactive */}
            <div className="ml-4 flex items-center gap-2 px-3 py-1.5 bg-zinc-700/50 rounded-lg">
              <StarRating 
                rating={userRating || averageRating}
                size="sm"
                interactive={true}
                onRate={handleRate}
              />
              <span className="text-xs text-zinc-400">
                {averageRating.toFixed(1)} ({ratingsCount})
              </span>
              {ratingLoading && <Loader2 className="w-3 h-3 animate-spin text-amber-400" />}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Comments Toggle */}
            <button
              onClick={() => setShowComments(!showComments)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                showComments ? 'bg-indigo-500/20 text-indigo-400' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
              }`}
              title={isSpanish ? 'Comentarios' : 'Comments'}
            >
              <MessageSquare className="w-4 h-4" />
              {app.comments_count || 0}
            </button>
            <button
              onClick={handleLike}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                liked ? 'bg-red-500/20 text-red-400' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
              }`}
            >
              <Heart className={`w-4 h-4 ${liked ? 'fill-red-400' : ''}`} />
              {likesCount}
            </button>
            <button
              onClick={handleShare}
              className="p-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg transition-colors"
              title={isSpanish ? 'Compartir' : 'Share'}
            >
              <Share2 className="w-4 h-4 text-zinc-300" />
            </button>
            <button
              onClick={() => onClone(app)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 rounded-lg text-xs text-white transition-colors"
            >
              <Copy className="w-4 h-4" />
              {isSpanish ? 'Clonar' : 'Clone'}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>
        </div>
        
        {/* Content Area - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {/* App Preview */}
          <div className="h-[500px] bg-white">
            <iframe
              srcDoc={app.html_content}
              className="w-full h-full border-0"
              title={app.name}
              sandbox="allow-scripts allow-same-origin"
            />
          </div>
          
          {/* Comments Section */}
          {showComments && (
            <AppComments appId={app.id} isAuthenticated={isAuthenticated} />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// Helper function to generate full HTML from JSX code
const generateFullHTML = (jsxCode) => {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <script src="https://cdn.tailwindcss.com"></script>
  <script src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
  <script src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
  <script src="https://unpkg.com/prop-types@15/prop-types.min.js"></script>
  <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
  <script src="https://unpkg.com/recharts@2/umd/Recharts.js"></script>
  <script src="https://unpkg.com/lucide@latest"></script>
  <style>
    body { margin: 0; font-family: system-ui, -apple-system, sans-serif; background: #18181b; color: #fff; }
    #root { min-height: 100dvh; padding: 1rem; padding-bottom: 3rem; }
    .damyxia-watermark {
      position: fixed;
      bottom: 8px;
      right: 12px;
      display: flex;
      align-items: center;
      gap: 6px;
      opacity: 0.6;
      transition: opacity 0.3s;
      z-index: 9999;
    }
    .damyxia-watermark:hover { opacity: 1; }
    .damyxia-watermark img { width: 20px; height: 20px; border-radius: 50%; box-shadow: 0 0 8px rgba(99, 102, 241, 0.4); }
    .damyxia-watermark span { font-size: 10px; font-weight: 500; background: linear-gradient(90deg, #a78bfa, #6366f1); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
  </style>
</head>
<body>
  <div id="root"></div>
  <div class="damyxia-watermark">
    <img src="/icons/icon-192x192.png" alt="DamyxIA" />
    <span>by DamyxIA</span>
  </div>
  <script type="text/babel">
    const { useState, useEffect, useRef } = React;
    const { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } = Recharts;
    const createIcon = (name) => {
      return (props) => {
        const ref = useRef(null);
        useEffect(() => {
          if (ref.current && lucide[name]) {
            ref.current.innerHTML = '';
            const icon = lucide.createElement(lucide[name]);
            icon.setAttribute('width', props.size || 24);
            icon.setAttribute('height', props.size || 24);
            if (props.className) icon.setAttribute('class', props.className);
            ref.current.appendChild(icon);
          }
        }, []);
        return <span ref={ref} style={{display: 'inline-flex'}} />;
      };
    };
    const TrendingUp = createIcon('TrendingUp');
    const TrendingDown = createIcon('TrendingDown');
    const DollarSign = createIcon('DollarSign');
    const Users = createIcon('Users');
    const ShoppingCart = createIcon('ShoppingCart');
    const Package = createIcon('Package');
    const Activity = createIcon('Activity');
    ${jsxCode}
    const possibleComponents = [
      typeof App !== 'undefined' ? App : null,
      typeof Dashboard !== 'undefined' ? Dashboard : null,
      typeof Main !== 'undefined' ? Main : null,
      typeof SalesDashboard !== 'undefined' ? SalesDashboard : null,
      typeof AnalyticsDashboard !== 'undefined' ? AnalyticsDashboard : null,
      typeof AdminPanel !== 'undefined' ? AdminPanel : null,
      typeof Counter !== 'undefined' ? Counter : null,
    ].filter(c => c !== null && typeof c === 'function');
    if (possibleComponents.length > 0) {
      ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(possibleComponents[0]));
    }
  </script>
</body>
</html>`;
};

// ==================== PUBLISH APP MODAL ====================
const PublishAppModal = ({ isOpen, onClose, onPublish, initialCode = '', conversationId }) => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const [loading, setLoading] = useState(false);
  const [generatingThumbnail, setGeneratingThumbnail] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const previewIframeRef = useRef(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    tags: '',
    is_public: true
  });

  // Generate full HTML when modal opens
  const fullHtml = initialCode ? generateFullHTML(initialCode) : '';

  // Reset preview ready state when code changes
  useEffect(() => {
    setPreviewReady(false);
    if (initialCode && isOpen) {
      // Give iframe time to render
      const timer = setTimeout(() => setPreviewReady(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [initialCode, isOpen]);

  // Generate thumbnail from iframe
  const generateThumbnail = async () => {
    if (!previewIframeRef.current) return null;
    
    try {
      setGeneratingThumbnail(true);
      const html2canvas = (await import('html2canvas')).default;
      
      // Get iframe document
      const iframe = previewIframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      
      if (!iframeDoc || !iframeDoc.body) {
        console.warn('Could not access iframe document');
        return null;
      }
      
      // Capture the iframe content
      const canvas = await html2canvas(iframeDoc.body, {
        width: 400,
        height: 300,
        scale: 1,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#18181b',
        logging: false
      });
      
      // Convert to base64 (low quality for smaller size)
      return canvas.toDataURL('image/jpeg', 0.6);
    } catch (error) {
      console.warn('Thumbnail generation failed:', error);
      return null;
    } finally {
      setGeneratingThumbnail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !initialCode.trim()) {
      toast.error(isSpanish ? 'Nombre y código son requeridos' : 'Name and code are required');
      return;
    }
    
    setLoading(true);
    try {
      // Generate thumbnail automatically
      const thumbnail = await generateThumbnail();
      
      await onPublish({
        name: form.name.trim(),
        description: form.description.trim(),
        html_content: fullHtml,
        jsx_code: initialCode,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        is_public: form.is_public,
        conversation_id: conversationId,
        thumbnail: thumbnail
      });
      onClose();
      setForm({ name: '', description: '', tags: '', is_public: true });
    } catch (error) {
      // Error handled in parent
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      
      <div 
        className="relative w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-zinc-700 bg-gradient-to-r from-indigo-600/20 to-purple-600/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/30 flex items-center justify-center">
              <Rocket className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {isSpanish ? 'Publicar mi App' : 'Publish my App'}
              </h2>
              <p className="text-xs text-zinc-400">
                {isSpanish ? 'Tu app estará disponible online' : 'Your app will be available online'}
              </p>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-1.5">
              {isSpanish ? 'Nombre de la App' : 'App Name'} *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm({...form, name: e.target.value})}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
              placeholder={isSpanish ? 'Mi increíble app' : 'My awesome app'}
              required
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-1.5">
              {isSpanish ? 'Descripción' : 'Description'}
            </label>
            <textarea
              value={form.description}
              onChange={e => setForm({...form, description: e.target.value})}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500 resize-none"
              rows={3}
              placeholder={isSpanish ? '¿De qué trata tu app?' : 'What is your app about?'}
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-1.5">
              {isSpanish ? 'Tags (separados por coma)' : 'Tags (comma separated)'}
            </label>
            <input
              type="text"
              value={form.tags}
              onChange={e => setForm({...form, tags: e.target.value})}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
              placeholder="dashboard, analytics, react"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
            <div className="flex items-center gap-2">
              {form.is_public ? <Globe className="w-4 h-4 text-green-400" /> : <Lock className="w-4 h-4 text-yellow-400" />}
              <span className="text-sm text-zinc-300">
                {form.is_public ? (isSpanish ? 'Pública' : 'Public') : (isSpanish ? 'Privada' : 'Private')}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setForm({...form, is_public: !form.is_public})}
              className={`relative w-10 h-5 rounded-full transition-colors ${form.is_public ? 'bg-green-500' : 'bg-zinc-600'}`}
            >
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${form.is_public ? 'left-5' : 'left-0.5'}`} />
            </button>
          </div>
          
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg text-sm transition-colors"
            >
              {isSpanish ? 'Cancelar' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={loading || !form.name.trim()}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Rocket className="w-4 h-4" />
                  {isSpanish ? 'Publicar' : 'Publish'}
                </>
              )}
            </button>
          </div>
        </form>
        
        {/* Hidden iframe for thumbnail generation */}
        {initialCode && (
          <iframe
            ref={previewIframeRef}
            srcDoc={fullHtml}
            className="absolute -left-[9999px] w-[400px] h-[300px]"
            title="Thumbnail Preview"
            // P24.2 · consistente con el iframe principal del Workspace.
            sandbox="allow-scripts allow-same-origin"
            onLoad={() => setPreviewReady(true)}
          />
        )}
      </div>
    </div>,
    document.body
  );
};

// ==================== MAIN COMPONENT ====================
const MyApps = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, published, draft, archived
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showViewer, setShowViewer] = useState(false);

  // 🟥 Fix UX 2026-02-19 — navegar a la página /my-apps donde está el
  // Builder, el "Crear app" y el botón "Editor" por app del workspace.
  const goToWorkspace = () => {
    onClose?.();
    navigate('/my-apps');
  };

  useEffect(() => {
    if (isOpen) {
      loadApps();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadApps = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/published-apps/my-apps`, {
      });
      setApps(response.data.apps || []);
    } catch (error) {
      toast.error(isSpanish ? 'Error al cargar apps' : 'Error loading apps');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (app) => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/published-apps/view/${app.slug}`, {});
      setSelectedApp(response.data.app);
      setShowViewer(true);
    } catch (error) {
      toast.error(isSpanish ? 'Error al cargar app' : 'Error loading app');
    }
  };

  const handleEdit = () => {
    // 🟥 CUADRO NEGRO MAESTRO (sec 2) — PROHIBIDO abrir el Builder desde una
    // tarjeta. Se navega a Mis Apps; el Builder visual sólo se abre desde
    // /builder-projects con el botón manual aislado.
    onClose?.();
    navigate('/my-apps');
  };

  const handleClone = async (app) => {
    try {
      const token = getToken();
      await axios.post(
        `${API_URL}/api/published-apps/${app.id}/clone`,
        {},
        {}
      );
      toast.success(isSpanish ? '¡App clonada!' : 'App cloned!');
      loadApps();
      setShowViewer(false);
    } catch (error) {
      toast.error(isSpanish ? 'Error al clonar' : 'Error cloning app');
    }
  };

  const handleDelete = async (app) => {
    if (!window.confirm(isSpanish ? '¿Eliminar esta app?' : 'Delete this app?')) return;
    
    try {
      const token = getToken();
      await axios.delete(`${API_URL}/api/published-apps/${app.id}`, {
      });
      toast.success(isSpanish ? 'App eliminada' : 'App deleted');
      loadApps();
    } catch (error) {
      toast.error(isSpanish ? 'Error al eliminar' : 'Error deleting app');
    }
  };

  const handleTogglePublic = async (app) => {
    try {
      const token = getToken();
      await axios.put(
        `${API_URL}/api/published-apps/${app.id}`,
        { is_public: !app.is_public },
        {}
      );
      toast.success(app.is_public 
        ? (isSpanish ? 'App ahora es privada' : 'App is now private')
        : (isSpanish ? 'App ahora es pública' : 'App is now public')
      );
      loadApps();
    } catch (error) {
      toast.error(isSpanish ? 'Error al actualizar' : 'Error updating app');
    }
  };

  const filteredApps = apps.filter(app => {
    if (filter !== 'all' && app.status !== filter) return false;
    if (searchQuery && !app.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      
      <div 
        className="relative w-full max-w-4xl h-[80vh] bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-700 bg-gradient-to-r from-indigo-900/30 to-purple-900/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/30 flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {isSpanish ? 'Mis Apps' : 'My Apps'}
                </h2>
                <p className="text-xs text-zinc-400">
                  {apps.length} {isSpanish ? 'apps publicadas' : 'published apps'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={goToWorkspace}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs rounded-lg transition-colors"
                title={isSpanish ? 'Crear app y abrir Builder' : 'Create app & open Builder'}
                data-testid="myapps-create-app-btn"
              >
                <Plus className="w-4 h-4" />
                {isSpanish ? 'Crear app' : 'Create app'}
              </button>
              <button onClick={onClose} className="p-2 hover:bg-zinc-700 rounded-lg transition-colors" data-testid="myapps-close-btn">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
          </div>
          
          {/* Filters */}
          <div className="flex items-center gap-3 mt-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={isSpanish ? 'Buscar apps...' : 'Search apps...'}
                className="w-full pl-9 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-indigo-500"
              />
            </div>
            <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-1">
              {['all', 'published', 'draft', 'archived'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                    filter === f ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  {f === 'all' ? (isSpanish ? 'Todas' : 'All') : 
                   f === 'published' ? (isSpanish ? 'Publicadas' : 'Published') :
                   f === 'draft' ? (isSpanish ? 'Borradores' : 'Drafts') :
                   (isSpanish ? 'Archivadas' : 'Archived')}
                </button>
              ))}
            </div>
            <button
              onClick={loadApps}
              className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
              title={isSpanish ? 'Actualizar' : 'Refresh'}
            >
              <RefreshCw className="w-4 h-4 text-zinc-400" />
            </button>
          </div>
        </div>
        
        {/* Apps Grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
            </div>
          ) : filteredApps.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center" data-testid="myapps-empty-state">
              <FolderOpen className="w-16 h-16 text-zinc-700 mb-4" />
              <h3 className="text-lg font-medium text-zinc-400 mb-2">
                {isSpanish ? 'No hay apps' : 'No apps yet'}
              </h3>
              <p className="text-sm text-zinc-500 max-w-sm mb-6">
                {isSpanish 
                  ? 'Creá tu primera app con DamyxIA y abrila en el Builder para empezar a importar recetas del Marketplace.'
                  : 'Create your first app with DamyxIA and open it in the Builder to start importing recipes from the Marketplace.'}
              </p>
              <button
                onClick={goToWorkspace}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white text-sm font-medium rounded-lg transition-colors shadow-lg shadow-indigo-500/30"
                data-testid="myapps-empty-create-cta"
              >
                <Plus className="w-4 h-4" />
                {isSpanish ? 'Crear mi primera app' : 'Create my first app'}
              </button>
              <button
                onClick={goToWorkspace}
                className="mt-2 text-[11px] text-zinc-500 hover:text-zinc-300 underline underline-offset-2 transition-colors"
                data-testid="myapps-empty-manage-link"
              >
                {isSpanish ? 'o gestioná todas tus apps del Builder →' : 'or manage all your Builder apps →'}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredApps.map(app => (
                <AppCard
                  key={app.id}
                  app={app}
                  onView={handleView}
                  onEdit={handleEdit}
                  onClone={handleClone}
                  onDelete={handleDelete}
                  onTogglePublic={handleTogglePublic}
                  isOwner={true}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* App Viewer Modal */}
      <AppViewerModal
        app={selectedApp}
        isOpen={showViewer}
        onClose={() => setShowViewer(false)}
        onClone={handleClone}
      />
    </div>,
    document.body
  );
};

export { MyApps, PublishAppModal, AppViewerModal, AppCard };
export default MyApps;
